import PropTypes from "prop-types";
import { Tooltip } from "antd";
import { Link } from "react-router-dom";
import { RiErrorWarningLine } from "react-icons/ri";

import { CopyButton, RowActionsMenu, TruncatedText } from "../../../components/data-table";
import { formatReservationDate, formatReservationTime } from "../list/reservationFormatters";

function ReservationCard({
    reservation,
    statusBadge,
    menuItems,
    onMenuClick,
    t,
    showCarNeededWarning = false,
    showOverdueWarning = false,
}) {
    const vin = reservation?.carSelected?.VIN;

    return (
        <article className="sc-copy-scope rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <span className="inline-flex min-w-0 items-center gap-1 text-sm font-medium">
                    <Link to={`/reservations/${reservation.id}`} className="sc-table-link sc-mono">
                        {reservation.id}
                    </Link>
                    <CopyButton value={reservation.id} />
                    {showCarNeededWarning && !reservation?.carSelected ? (
                        <Tooltip title={t("_upcoming.carNeededWarning")}>
                            <RiErrorWarningLine size={16} color="#ff8800" aria-label={t("_upcoming.carNeededWarning")} />
                        </Tooltip>
                    ) : null}
                    {showOverdueWarning ? (
                        <Tooltip title={t("_ongoing.overdueReservation")}>
                            <RiErrorWarningLine size={16} color="#ff8800" aria-label={t("_ongoing.overdueReservation")} />
                        </Tooltip>
                    ) : null}
                </span>
                <RowActionsMenu items={menuItems} onClick={onMenuClick} label={t("action")} />
            </div>

            <TruncatedText className="mt-2 text-left text-lg font-bold">
                <Link to={`/customers/${reservation?.userUID}`} className="text-[#2F2F46] hover:text-[#5D5DF6]">
                    {reservation.customerName || t("notAvailable")}
                </Link>
            </TruncatedText>

            <div className="mt-4 grid grid-cols-2 gap-4 border-b border-gray-100 pb-4">
                <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t("pickupTime")}</p>
                    <p className="mt-1 text-sm font-medium text-[#2F2F46]">
                        {formatReservationDate(reservation.pickupDate)}
                    </p>
                    <p className="text-xs text-gray-500">{formatReservationTime(reservation.pickupDate)}</p>
                </div>
                <div className="border-l border-gray-100 pl-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{t("dropoffTime")}</p>
                    <p className="mt-1 text-sm font-medium text-[#2F2F46]">
                        {formatReservationDate(reservation.dropoffDate)}
                    </p>
                    <p className="text-xs text-gray-500">{formatReservationTime(reservation.dropoffDate)}</p>
                </div>
            </div>

            <div className="mt-4 flex items-end justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                        {t("iadvreq.carType")} / {t("vin")}
                    </p>
                    <TruncatedText className="mt-1 text-sm text-[#2F2F46]">
                        {reservation?.carType || t("notAvailable")}
                    </TruncatedText>
                    {vin ? (
                        <span className="flex items-center gap-1">
                            <Link to={`/cardetails/${vin}`} className="sc-table-link sc-mono truncate text-xs">
                                {vin}
                            </Link>
                            <CopyButton value={vin} />
                        </span>
                    ) : (
                        <span className="text-xs text-gray-400">{t("carNotAssigned")}</span>
                    )}
                </div>
                <span
                    className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-semibold uppercase ${statusBadge.className}`}>
                    {statusBadge.label}
                </span>
            </div>
        </article>
    );
}

ReservationCard.propTypes = {
    reservation: PropTypes.object.isRequired,
    statusBadge: PropTypes.shape({
        label: PropTypes.string.isRequired,
        className: PropTypes.string.isRequired,
    }).isRequired,
    menuItems: PropTypes.arrayOf(PropTypes.object).isRequired,
    onMenuClick: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    showCarNeededWarning: PropTypes.bool,
    showOverdueWarning: PropTypes.bool,
};

export default ReservationCard;
